// O IO da Central Inteligente IBS-CBS (000230/000231), da Pós RT — zero regra.
//
// Nenhum `select` cru: as sete tabelas `rt_*` têm `revoke all` para
// `authenticated`, e a leitura sai por função com portão. É a cicatriz da
// 000136, em que a aba Secretaria vazou os 6.338 emitidos de todo mundo por ler
// a tabela direto, confiando na RLS.
//
// Quem decide se o aluno pode é `rt_pode_usar` → `abrir_card`, no banco. Aqui só
// se pede e se traduz o motivo da recusa para português de gente.
import { supabase } from "./supabase";

export type Norma = {
  id: string;
  slug: string;
  nome: string;
  tipo: string;
  numero: string | null;
  orgao: string | null;
  data_publicacao: string | null;
  link_oficial: string | null;
  ementa: string | null;
  artigos: number;
};

export type Tema = {
  id: string;
  slug: string;
  nome: string;
  descricao: string | null;
  artigos: number;
};

export type ChecklistResumo = {
  id: string;
  slug: string;
  titulo: string;
  descricao: string | null;
  publico: string | null;
  icone: string | null;
  itens: number;
};

export type Faq = { pergunta: string; resposta: string };

export type Acervo = {
  normas: Norma[];
  temas: Tema[];
  checklists: ChecklistResumo[];
  faq: Faq[];
};

/** A linha da lista — o resumo vem CORTADO em 400 caracteres pelo banco. */
export type ArtigoDaLista = {
  id: string;
  rotulo: string | null;
  ordem: number;
  norma_id: string;
  norma: string;
  norma_tipo: string;
  norma_numero: string | null;
  livro: string | null;
  titulo: string | null;
  capitulo: string | null;
  secao: string | null;
  subsecao: string | null;
  importancia: "alto" | "medio" | "baixo";
  palavras_chave: string[];
  resumo: string | null;
};

export type ArtigoInteiro = {
  id: string;
  rotulo: string | null;
  ordem: number;
  livro: string | null;
  titulo: string | null;
  capitulo: string | null;
  secao: string | null;
  subsecao: string | null;
  texto_literal: string;
  resumo_tecnico: string | null;
  explicacao_pratica: string | null;
  exemplo_aplicado: string | null;
  impacto_contador: string | null;
  impacto_empresa: string | null;
  obrigacoes: string | null;
  riscos: string | null;
  palavras_chave: string[];
  importancia: "alto" | "medio" | "baixo";
};

export type FichaDoArtigo = {
  artigo: ArtigoInteiro;
  norma: {
    id: string;
    slug: string;
    nome: string;
    tipo: string;
    numero: string | null;
    orgao: string | null;
    link_oficial: string | null;
  };
  temas: { id: string; slug: string; nome: string }[];
  anterior: { id: string; rotulo: string | null } | null;
  proximo: { id: string; rotulo: string | null } | null;
};

export type ItemDeChecklist = {
  id: string;
  ordem: number;
  titulo: string;
  detalhe: string | null;
  artigo_id: string | null;
  artigo_rotulo: string | null;
};

export type Checklist = {
  checklist: { id: string; slug: string; titulo: string; descricao: string | null; publico: string | null };
  itens: ItemDeChecklist[];
};

/** O resultado de qualquer leitura: ou veio, ou a recusa DIZ por quê. */
export type Resposta<T> = { ok: true; dado: T } | { ok: false; motivo: string };

/**
 * A frase de cada recusa. As chaves são as que o banco devolve — trocar uma aqui
 * sem trocar lá deixa a tela muda, que é pior que uma mensagem genérica.
 */
export function mensagemDaRecusa(motivo: string | null | undefined): string {
  switch (motivo) {
    case "sem-perfil":
      return "Não encontrei o seu cadastro. Saia e entre de novo.";
    case "sem-acesso":
    case "card-nao-e-seu":
      return "A Central IBS-CBS não faz parte dos cursos que você tem hoje.";
    case "trancado":
    case "contrato-pendente":
      return "O curso desta ferramenta está aguardando a assinatura do contrato. Assine na página do curso para liberar.";
    case "artigo-nao-encontrado":
      return "Não achei este dispositivo. Ele pode ter saído do acervo — volte à norma e escolha de novo.";
    case "checklist-nao-encontrado":
      return "Não achei este roteiro.";
    default:
      return "Não consegui carregar agora. Tente de novo em alguns segundos.";
  }
}

/** Uma ida só: normas, temas, checklists e FAQ com as contagens. */
export async function lerAcervo(curso: string): Promise<Resposta<Acervo>> {
  const { data, error } = await supabase.rpc("rt_acervo", { p_curso: curso });
  if (error) return { ok: false, motivo: "erro" };
  const r = (data ?? {}) as Record<string, unknown>;
  if (!r.ok) return { ok: false, motivo: String(r.motivo ?? "erro") };
  return {
    ok: true,
    dado: {
      normas: (r.normas ?? []) as Norma[],
      temas: (r.temas ?? []) as Tema[],
      checklists: (r.checklists ?? []) as ChecklistResumo[],
      faq: (r.faq ?? []) as Faq[],
    },
  };
}

/**
 * A lista de artigos, recortada. O `total` vem junto da página de propósito:
 * lista paginada sem o total faz a tela mentir sobre o que existe (a cicatriz do
 * caso 10 do teste 56, em que o pedido novo caía fora do lote e ninguém via).
 */
export async function lerArtigos(
  curso: string,
  recorte: {
    norma?: string | null;
    tema?: string | null;
    termo?: string | null;
    limite?: number;
    offset?: number;
    /**
     * Para ABORTAR o pedido anterior quando o recorte muda.
     *
     * Não é otimização: a tela da norma pede a lei INTEIRA (620 dispositivos,
     * ~280 kB), e quem digita na busca dispara o segundo pedido enquanto o
     * primeiro ainda está em voo. Sem abortar, o segundo espera o primeiro
     * terminar — e a prova de tela mediu isso como busca que não responde em
     * oito segundos, passando numa rodada e falhando na seguinte. Teste que
     * passa numa rodada e falha na outra é defeito, não azar.
     */
    sinal?: AbortSignal;
  },
): Promise<Resposta<{ total: number; artigos: ArtigoDaLista[]; aviso?: string }>> {
  let consulta = supabase.rpc("rt_lista_artigos", {
    p_curso: curso,
    p_norma: recorte.norma ?? null,
    p_tema: recorte.tema ?? null,
    p_termo: recorte.termo ?? null,
    p_limite: recorte.limite ?? 60,
    p_offset: recorte.offset ?? 0,
  });
  if (recorte.sinal) consulta = consulta.abortSignal(recorte.sinal);
  const { data, error } = await consulta;
  // Abortar é escolha da tela, não falha: quem cancelou já pediu outra coisa.
  if (recorte.sinal?.aborted) return { ok: false, motivo: "cancelado" };
  if (error) return { ok: false, motivo: "erro" };
  const r = (data ?? {}) as Record<string, unknown>;
  if (!r.ok) return { ok: false, motivo: String(r.motivo ?? "erro") };
  return {
    ok: true,
    dado: {
      total: Number(r.total ?? 0),
      artigos: (r.artigos ?? []) as ArtigoDaLista[],
      aviso: typeof r.aviso === "string" ? r.aviso : undefined,
    },
  };
}

export async function lerArtigo(curso: string, id: string): Promise<Resposta<FichaDoArtigo>> {
  const { data, error } = await supabase.rpc("rt_artigo", { p_curso: curso, p_artigo: id });
  if (error) return { ok: false, motivo: "erro" };
  const r = (data ?? {}) as Record<string, unknown>;
  if (!r.ok) return { ok: false, motivo: String(r.motivo ?? "erro") };
  return {
    ok: true,
    dado: {
      artigo: r.artigo as ArtigoInteiro,
      norma: r.norma as FichaDoArtigo["norma"],
      temas: (r.temas ?? []) as FichaDoArtigo["temas"],
      anterior: (r.anterior ?? null) as FichaDoArtigo["anterior"],
      proximo: (r.proximo ?? null) as FichaDoArtigo["proximo"],
    },
  };
}

export async function lerChecklist(curso: string, slug: string): Promise<Resposta<Checklist>> {
  const { data, error } = await supabase.rpc("rt_checklist", { p_curso: curso, p_slug: slug });
  if (error) return { ok: false, motivo: "erro" };
  const r = (data ?? {}) as Record<string, unknown>;
  if (!r.ok) return { ok: false, motivo: String(r.motivo ?? "erro") };
  return {
    ok: true,
    dado: { checklist: r.checklist as Checklist["checklist"], itens: (r.itens ?? []) as ItemDeChecklist[] },
  };
}
